import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ChevronRight, Search } from "lucide-react";
import { renderFieldIcon } from "../../../../fields/FieldList";
import { FilterRuleProps } from "../../../../main/component/filter/AdvancedFilter";

type FieldOption = {
  label: string;
  value: string;
  data: any;
};

type FieldPickerProps = {
  fields: FieldOption[];
  onSelect: (rule: FilterRuleProps) => void;
  onClose: () => void;
};

/**
 * Searchable field picker popover content.
 * Shows fields with icons and value counts.
 */
const FieldPicker = ({ fields, onSelect, onClose }: FieldPickerProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const ruleReducer = useSelector((state: any) => state.ruleReducer);

  const filtered = fields.filter((f) =>
    search ? f.label.toLowerCase().includes(search.toLowerCase()) : true
  );

  return (
    <div className="w-56">
      {/* Search input */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200">
        <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
        <input
          autoFocus
          type="text"
          className="w-full text-sm bg-transparent outline-none placeholder:text-gray-400"
          placeholder={t("Search attributes...")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Field list */}
      <div className="max-h-72 overflow-y-auto py-1">
        {filtered.map((field) => {
          const values = ruleReducer.get(field.value)?.toJS() || [];
          const count = values.length;

          return (
            <button
              key={field.value}
              className="w-full flex items-center justify-between px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => {
                onSelect({
                  operator: "",
                  fieldType: field.data?.type || "",
                  fieldId: field.value,
                  fieldValue: [],
                });
                onClose();
              }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="shrink-0">{renderFieldIcon(field.data || {})}</span>
                <span className="truncate text-gray-800">{t(field.label)}</span>
              </div>
              {count > 0 && (
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  <span className="text-xs text-gray-400">{count}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                </div>
              )}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="px-3 py-4 text-sm text-gray-400 text-center">
            {t("No attributes found")}
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldPicker;
